import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Settings, Save, RotateCcw } from 'lucide-react';

const UserSettings = ({ open, onOpenChange }) => {
  const [preferences, setPreferences] = useState(null);
  const [aiInstructions, setAiInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const defaultInstructions = `1. Search for significant life events, people and places.
2. If many images are taken at the same scene, select the best images for clarity, smiles and eyes open in the image.
3. Skip MEMES and screenshots.
4. Try to pick out significant images and stories that are worthy of a photoalbum/keepsake.
5. Focus on images with emotional value and memorable moments.`;

  useEffect(() => {
    if (open) {
      fetchPreferences();
    }
  }, [open]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/preferences`);
      setPreferences(response.data);
      setAiInstructions(response.data.ai_instructions);
    } catch (error) {
      toast.error('Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/preferences`, {
        ai_instructions: aiInstructions
      });
      toast.success('Preferences saved successfully!');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setAiInstructions(defaultInstructions);
    toast.info('Instructions reset to default');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-settings-dialog" className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            AI Photo Selection Settings
          </DialogTitle>
          <DialogDescription>
            Customize how AI analyzes and selects your photos for albums
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label htmlFor="ai-instructions" className="text-base font-semibold">
                AI Selection Instructions
              </Label>
              <p className="text-sm text-gray-600">
                Tell the AI what to look for when analyzing your photos. These instructions help the AI 
                identify the most meaningful images for your albums.
              </p>
              <Textarea
                data-testid="ai-instructions-input"
                id="ai-instructions"
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                rows={12}
                className="font-mono text-sm"
                placeholder="Enter your custom instructions..."
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Tips for effective instructions:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Be specific about what makes a photo significant to you</li>
                <li>• Mention any criteria for photo quality (clarity, composition, etc.)</li>
                <li>• List any types of images to skip (memes, screenshots, duplicates)</li>
                <li>• Describe the emotional tone you're looking for</li>
                <li>• Include any specific events or themes to prioritize</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                data-testid="reset-instructions-btn"
                variant="outline"
                onClick={handleReset}
                className="flex-1"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Default
              </Button>
              <Button
                data-testid="save-preferences-btn"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-gradient-to-r from-amber-600 to-rose-500 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Instructions'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserSettings;
